import { useState, useCallback, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * useGenerationQueue — client-side, fire-and-forget parallel generation queue.
 *
 * Each entry: { id, mode ("image"|"video"|"v2v"|"turnaround"), prompt,
 *               status ("queued"|"generating"|"done"|"error"),
 *               kind ("image"|"video"), media_url, generation_id, error,
 *               created_at, params }
 *
 * `enqueue({ mode, prompt, params })` immediately appends an optimistic item
 * to the feed and kicks off the network call in the background. Multiple
 * enqueue()s can run simultaneously (browser's default HTTP concurrency).
 *
 * `regenerateN(item, n)` fans out N parallel enqueues of the same prompt/params
 * (used by the "×4 Variations" action to A/B a single prompt).
 *
 * Persistence: the last 20 **completed** items (status `done` or `error`) are
 * mirrored to `localStorage["geneo:feedQueue"]` on every change, and restored
 * on mount so a page refresh never wipes the day's work. In-flight items
 * (`generating`, `queued`) are dropped on refresh since we cannot resume them.
 */

const STORAGE_KEY = "geneo:feedQueue";
const STORAGE_LIMIT = 20;

const uuid = () =>
  (crypto && crypto.randomUUID) ? crypto.randomUUID() : `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const loadPersisted = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    // Only rehydrate terminal items — we can't resume in-flight ones.
    // Sort ascending by created_at so chat-log ordering is preserved on reload.
    const terminal = arr.filter((it) => it && (it.status === "done" || it.status === "error"));
    terminal.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    return terminal.slice(-STORAGE_LIMIT);
  } catch { return []; }
};

const persist = (items) => {
  try {
    const persistable = items
      .filter((it) => it.status === "done" || it.status === "error")
      .slice(-STORAGE_LIMIT); // keep the most recent tail
    localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
  } catch { /* quota exceeded or private mode — ignore */ }
};

export function useGenerationQueue() {
  const [items, setItems] = useState(() => loadPersisted());
  const seq = useRef(0);

  // Mirror to localStorage whenever items change
  useEffect(() => { persist(items); }, [items]);

  const patch = useCallback((id, updater) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...updater(it) } : it)));
  }, []);

  const enqueue = useCallback(async ({ mode, prompt, params = {} }) => {
    const id = uuid();
    const created_at = new Date().toISOString();
    // Chronological order: newest items append at the END of the feed (chat-log style)
    setItems((prev) => [...prev, { id, mode, prompt, status: "generating", created_at, kind: modeToKind(mode), params }]);
    seq.current += 1;

    try {
      const { endpoint, body } = buildRequest(mode, prompt, params);
      const { data } = await api.post(endpoint, body);
      // If the backend queued this as a background job, poll for status.
      // Async endpoints return `{job_id, status:"queued", kind}` in ms; every
      // other endpoint returns the fully-materialised generation directly.
      if (data && data.job_id && data.status && data.status !== "completed") {
        patch(id, () => ({ status: "processing", job_id: data.job_id, progress: { step: 0, total: 1, label: "Queued" } }));
        const finalData = await pollJob(data.job_id, (job) => {
          patch(id, () => ({ status: "processing", progress: job.progress || { step: 0, total: 1, label: "Working" } }));
        });
        const extras = extractResult(mode, finalData);
        patch(id, () => ({ status: "done", progress: null, ...extras, generation_id: finalData.generation_id }));
        toast.success(labelFor(mode) + " ready");
        return { id, ok: true, data: finalData };
      }
      const extras = extractResult(mode, data);
      patch(id, () => ({ status: "done", ...extras, generation_id: data.generation_id }));
      toast.success(labelFor(mode) + " ready");
      return { id, ok: true, data };
    } catch (e) {
      // Refund-aware error surface: when the backend returns a structured
      // detail like `{code, message, refunded, action}` (fal / seedance
      // billing failures), pull the friendly message so the user sees
      // "credits refunded" instead of a raw stack. Otherwise fall back to
      // the string detail.
      const detail = e?.response?.data?.detail || e?.jobError || null;
      const isRefund = detail && typeof detail === "object" && Number.isFinite(detail.refunded);
      const msg = isRefund
        ? detail.message
        : (typeof detail === "string" ? detail : (detail?.message || e?.message || "Generation failed"));
      patch(id, () => ({ status: "error", error: msg, refunded: isRefund ? detail.refunded : 0, progress: null }));
      if (isRefund) {
        // Broadcast so Navbar / balance pill can re-fetch the fresh balance.
        try { window.dispatchEvent(new CustomEvent("credits:updated", { detail: { refunded: detail.refunded } })); } catch {}
        toast.error(msg, { duration: 6000 });
      } else {
        toast.error(msg);
      }
      return { id, ok: false, error: msg };
    }
  }, [patch]);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const regenerate = useCallback((item) => enqueue({ mode: item.mode, prompt: item.prompt, params: item.params }), [enqueue]);

  // Fan-out N parallel regenerations of the same prompt/params (for "×4 Variations").
  const regenerateN = useCallback(async (item, n = 4) => {
    const jobs = [];
    for (let i = 0; i < n; i++) {
      jobs.push(enqueue({ mode: item.mode, prompt: item.prompt, params: item.params }));
    }
    await Promise.allSettled(jobs);
  }, [enqueue]);

  // Reconciliation — fetch the user's completed OR failed async jobs from
  // the last 24h and merge any that aren't already in the local feed.
  // Called by Studio.jsx on mount so results survive a page refresh
  // (localStorage only keeps the last 20 terminal items and loses any
  // still-polling jobs when the tab was closed mid-generation).
  //
  // Failed jobs are included too — they come back with their refund info
  // intact so the feed can offer a RETRY pill. Idempotent: a job is
  // skipped if its `job_id` OR `generation_id` already appears in the
  // current feed items.
  const reconcileFromServer = useCallback(async () => {
    try {
      const kinds = "video,text-video,v2v,i2v,turnaround,style-sheet,orbit-views,turntable";
      // No `status=` filter so we pull BOTH `completed` and `failed`.
      const { data } = await api.get(`/jobs?kinds=${kinds}&since_hours=24&limit=50`);
      if (!Array.isArray(data) || !data.length) return;
      setItems((prev) => {
        const seenJob = new Set(prev.map((it) => it.job_id).filter(Boolean));
        const seenGen = new Set(prev.map((it) => it.generation_id).filter(Boolean));
        const additions = [];
        for (const job of data) {
          if (!job) continue;
          // Skip still-running jobs — they're owned by their own live poll
          // or already lost (we can't resume them cross-session).
          if (job.status !== "completed" && job.status !== "failed") continue;
          if (job.job_id && seenJob.has(job.job_id)) continue;
          const result = job.result || {};
          const gen_id = result.generation_id || result.id;
          if (gen_id && seenGen.has(gen_id)) continue;
          const mode = job.kind || job.type || "image";
          const isDone = job.status === "completed" && result && (result.video_url || result.image_url || result.views);
          const base = {
            id: job.job_id || `rec-${gen_id || uuid()}`,
            job_id: job.job_id,
            mode,
            kind: modeToKind(mode),
            prompt: result.prompt || (job.params && (job.params.prompt || job.params.motion_prompt)) || "",
            params: job.params || {},
            generation_id: gen_id,
            created_at: job.completed_at || job.updated_at || job.created_at || new Date().toISOString(),
            reconciled: true,
          };
          if (isDone) {
            const extras = extractResult(mode, result);
            additions.push({ ...base, status: "done", ...extras });
          } else {
            // Failed — pull the structured error back so the RETRY pill
            // and refund note render correctly.
            const err = job.error || {};
            const msg = typeof err === "string" ? err : (err.message || "Generation failed");
            const refunded = typeof err === "object" ? Number(err.refunded || 0) : 0;
            additions.push({ ...base, status: "error", error: msg, refunded });
          }
        }
        if (!additions.length) return prev;
        return [...prev, ...additions].sort(
          (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
        );
      });
    } catch {
      // Silent failure — reconciliation is a nicety, not a critical path.
    }
  }, []);

  return { items, enqueue, remove, clear, regenerate, regenerateN, setItems, reconcileFromServer };
}

/* ── helpers ─────────────────────────────────────────────────────────────── */

function modeToKind(mode) {
  if (mode === "video" || mode === "v2v" || mode === "i2v" || mode === "text-video" || mode === "turntable") return "video";
  // Everything else (image, style-sheet, turnaround composite, orbit-views) is an image card.
  return "image";
}

function labelFor(mode) {
  return {
    image: "Image", video: "Video", i2v: "Video", "text-video": "Video",
    v2v: "Restyled video", turnaround: "Turnaround",
    "style-sheet": "Style sheet",
    "orbit-views": "4-view orbit",
    turntable: "Turntable",
  }[mode] || "Generation";
}

function buildRequest(mode, prompt, params) {
  const { aspect_ratio = "16:9", resolution = "FHD", reference_urls,
          start_image_url, end_image_url, video_url, video_resolution = "720p",
          style, style_b, blend, prompt_augmented, model_version,
          source_generation_id, motion_prompt, duration,
          first_frame_url, last_frame_url, music_enabled } = params || {};
  const promptToSend = prompt_augmented || prompt;   // subject-lock identity guardrail if present
  const styleObj = style ? { style, style_b, blend } : {};
  if (mode === "i2v") {
    // Image-to-video (Seedance I2V) — hits the async /generate/video endpoint
    // which returns a job id. Frontend polls via pollJob.
    return {
      endpoint: "/generate/video",
      body: {
        generation_id: source_generation_id,
        motion_prompt: motion_prompt || promptToSend,
        duration: duration || 5,
        first_frame_url, last_frame_url,
        model_version,
        music_enabled,
        ...styleObj,
      },
    };
  }
  if (mode === "style-sheet") {
    return {
      endpoint: "/generate/style-sheet",
      body: {
        generation_id: source_generation_id,
        aspect_ratio,
      },
    };
  }
  if (mode === "image") {
    return {
      endpoint: "/generate/image",
      body: {
        prompt: promptToSend, aspect_ratio, resolution,
        reference_urls: reference_urls?.length ? reference_urls : undefined,
        ...styleObj,
      },
    };
  }
  if (mode === "video" || mode === "text-video") {
    return {
      endpoint: "/generate/text-video",
      body: {
        prompt: promptToSend, aspect_ratio, resolution: video_resolution,
        start_image_url, end_image_url,
        reference_urls: reference_urls?.length ? reference_urls : undefined,
        model_version,
        music_enabled,
        ...styleObj,
      },
    };
  }
  if (mode === "v2v") {
    const { duration: v2vDuration } = params || {};
    return {
      endpoint: "/generate/video-to-video",
      body: {
        prompt: promptToSend, video_url,
        duration: v2vDuration,
        aspect_ratio, resolution: video_resolution,
        reference_urls: reference_urls?.length ? reference_urls : undefined,
        first_frame_url: start_image_url, last_frame_url: end_image_url,
        music_enabled,
        ...styleObj,
      },
    };
  }
  if (mode === "turnaround") {
    return {
      endpoint: "/generate/turnaround",
      body: {
        prompt: promptToSend, aspect_ratio, resolution,
        reference_url: reference_urls?.[0],
        ...styleObj,
      },
    };
  }
  if (mode === "orbit-views") {
    return {
      endpoint: "/generate/orbit-views",
      body: { generation_id: source_generation_id, aspect_ratio },
    };
  }
  if (mode === "turntable") {
    return {
      endpoint: "/generate/turntable",
      body: { generation_id: source_generation_id, duration: duration || 5 },
    };
  }
  throw new Error(`Unknown mode: ${mode}`);
}

function extractResult(mode, data) {
  if (mode === "image" || mode === "style-sheet") return { media_url: data.image_url, sub_type: data.sub_type || (mode === "style-sheet" ? "style-sheet" : undefined) };
  if (mode === "video" || mode === "v2v" || mode === "i2v" || mode === "text-video") return { media_url: data.video_url };
  if (mode === "turnaround" || mode === "orbit-views") {
    const first = data.views && Object.values(data.views)[0];
    return { media_url: first || data.image_url, views: data.views, sub_type: mode };
  }
  if (mode === "turntable") {
    return { media_url: data.turntable_video_url || data.video_url };
  }
  return {};
}


/**
 * Poll GET /api/jobs/:jobId every 5 s until the job completes or fails,
 * then return the `result` doc. Reports live `progress` back through the
 * `onProgress` callback so the feed can render a step label ("Rendering
 * front view (2/4)"). Timeout hard-caps at 15 minutes — well past any
 * realistic Seedance / 4-view render — so a stuck job doesn't poll forever.
 *
 * Throws with `err.jobError` set to the structured failure detail so the
 * `useGenerationQueue` catch block can surface refund toasts identically
 * to sync failures.
 */
async function pollJob(jobId, onProgress, { intervalMs = 5000, timeoutMs = 15 * 60 * 1000 } = {}) {
  // Also exported at the bottom of this module for pages that want to poll
  // a single fire-and-forget job (e.g. AvatarStudio) without adopting the
  // full useGenerationQueue lifecycle.
  const started = Date.now();
  while (true) {
    if (Date.now() - started > timeoutMs) {
      const err = new Error("Generation timed out — try again in a moment.");
      err.jobError = { code: "job_timeout", message: err.message };
      throw err;
    }
    let job;
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      job = data;
    } catch (e) {
      // Network hiccup — back off and retry, don't kill the whole poll.
      await new Promise((r) => setTimeout(r, intervalMs));
      continue;
    }
    if (job.status === "completed") {
      return job.result || {};
    }
    if (job.status === "failed") {
      const err = new Error(job.error?.message || "Generation failed");
      err.jobError = job.error || { message: err.message };
      throw err;
    }
    // queued / processing — surface progress and keep polling
    try { onProgress && onProgress(job); } catch {}
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}


export { pollJob };
